import { format, isValid, parse } from "date-fns"
import { isEqual } from "lodash"
import { useSelector } from "react-redux"
import DateField from "../../../components/forms/DateField"
import CustomSearchInput from "../../../components/forms/CustomSearchInput"
import CustomSelect from "../../../components/forms/CustomSelect"
import FormLabel from "../../../components/forms/FormLabel"
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider"
import useClients from "../../../hooks/api-calls/useClients"
import useCompanies from "../../../hooks/api-calls/useCompanies"
import { FilterOptions } from "../../../hooks/useFilters"
import { RootState } from "../../../redux/store"
import { Button } from "rizzui"
import { PiTrashDuotone } from "react-icons/pi"
import useParks from "../../../hooks/api-calls/useParks"

const toIsoDateString = (value: string | Date): string => {
    if (value instanceof Date) return format(value, 'yyyy-MM-dd')
    const s = String(value)
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
    const d = parse(s, 'dd/MM/yyyy', new Date())
    return isValid(d) ? format(d, 'yyyy-MM-dd') : s
}

const parseFilterDateForPicker = (s: string | undefined): Date | null => {
    if (!s) return null
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(`${s}T12:00:00`)
    const d = parse(s, 'dd/MM/yyyy', new Date())
    return isValid(d) ? d : null
}


interface ParksFiltersProps {
    updateFilters: (filters: any) => void
    resetFilters: (limit: any) => void
    filters: FilterOptions
}

const ParksFilters: React.FC<ParksFiltersProps> = ({ filters, updateFilters, resetFilters }) => {

    const { userCan } = usePrivilege();
    const { getClientsList } = useClients();
    const { getCompaniesList } = useCompanies();
    const { user } = useSelector((state: RootState) => state.auth);
    const isSuperAdmin = user?.roles.includes('Superadministrador');
    const { getParksList } = useParks(true, {
        municipal: true
    });


    return (
        <>
            <CustomSelect
                isSearchable
                id={'status'}
                label="Estado"
                options={[
                    { label: 'Todos', value: null },
                    { label: 'Activos', value: true },
                    { label: 'Desactivados', value: false }
                ]}
                onChange={(e: any) => {
                    if (e.value === null) {
                        updateFilters({ status: null });
                        return;
                    }
                    updateFilters({ status: e.value });
                }}
                value={filters.filter_filters?.status !== null
                    ? { label: filters.filter_filters?.status ? 'Activo' : 'Desactivado', value: filters.filter_filters?.status }
                    : { label: 'Todos', value: null }}
            />

            <FormLabel label='Nombre'>
                <CustomSearchInput onSearch={(e) => updateFilters({ name: e })} defaultValue={filters.filter_filters?.name || ''} />
            </FormLabel>

            <FormLabel label='Término municipal'>
                <CustomSearchInput onSearch={(e) => updateFilters({ municipal: e })} defaultValue={filters.filter_filters?.municipal || ''} />
            </FormLabel>

            <CustomSelect
                isSearchable
                id={'municipal'}
                label="Término municipal"
                options={getParksList()}
                onChange={(e: any) => {
                    updateFilters({ municipal: e.value })
                }}
                value={
                    
                    filters.filter_filters?.location 
                        ? getParksList().filter((park: any) => park.value === filters.filter_filters?.municipal)
                        : null
                }
            />
            <FormLabel label='Alias'>
                <CustomSearchInput onSearch={(e) => updateFilters({ alias: e })} defaultValue={filters.filter_filters?.alias || ''} />
            </FormLabel>
   
            {isSuperAdmin ? (
                <CustomSelect
                    isSearchable
                    id={'company'}
                    label="Compañia"
                    options={getCompaniesList()}
                    onChange={(e: any) => { 
                        updateFilters({ company: e.value})
                    }}
                    value={
                        filters.filter_filters?.company
                            ? getCompaniesList().filter((company: any) => filters.filter_filters?.company.includes(company.value))
                            : null
                           
                    }
                    display={userCan('list', 'companies')}
                />
            ) : null}


            <CustomSelect
                isSearchable
                id={'client'}
                label="Cliente"
                options={getClientsList()}
                onChange={(e: any) => {
                    updateFilters({ client: e.value})
                }
                }
                value={
                    filters.filter_filters?.client
                        ? getClientsList().filter((client: any) => filters.filter_filters?.client.includes(client.value))
                        : null
                }
                display={userCan('admin_client', 'clients')}
            />

            <DateField
                isClearable
                className="w-full"
                label="Fecha de creación"
                placeholderText="Selecciona las fechas"
                selectedDateField={filters.filter_filters?.between_dates?.type || "createdAt"}
                startDate={parseFilterDateForPicker(filters.filter_filters?.between_dates?.startDate)}
                endDate={parseFilterDateForPicker(filters.filter_filters?.between_dates?.endDate)}
                onChange={(date: any) => {
                    const filters_between_dates = filters.filter_filters?.between_dates
                    if (!date) {
                        if (filters_between_dates) updateFilters({ between_dates: null })
                        return
                    }
                    const next = {
                        startDate: toIsoDateString(date[0]),
                        endDate: toIsoDateString(date[1]),
                        type: date[2] ?? 'createdAt',
                    }
                    if (!isEqual(filters_between_dates, next)) {
                        updateFilters({ between_dates: next })
                    }
                }}
            />

            <Button
                size="sm"
                onClick={() => {
                    resetFilters(50);
                    updateFilters({ between_dates: null });
                }}
                variant="flat"
                className="h-9 bg-gray-200/70"
            >
                <PiTrashDuotone className="me-1.5 h-[17px] w-[17px]" /> Limpiar
            </Button>   


        </>
    )
}

export default ParksFilters;